import React, { useCallback } from 'react';
import styled from 'styled-components';
import { FormationItemData } from '../../../shared/dto/EntityRepository';
import { convertS3UrlToCloudFrontUrl } from '../../../shared/aws/awsHelper';
import { useProjectElementListStore } from '../../../shared/store/projectElementStore';
import { TEXTBLOCK_CAPTION_MAX_LENGTH } from '../../../shared/constants/limits';
import {
  getFormationHorizontalAlignment,
  getFormationImageMode,
  getFormationImagePosition,
  getFormationVerticalAlignment,
  resolveFormationSlotImage,
} from '../../../shared/util/formationUtils';
import { removeFileExtensionWithinLimit } from '../../../shared/util/imageUtils';
import {
  FormationHorizontalAlignment,
  FormationImageMode,
  FormationVerticalAlignment,
} from '../../../shared/enum/EnumRepository';
import { useCoverImagePan } from '../../../shared/hooks/useCoverImagePan';
import { useFitImageSnapPan } from '../../../shared/hooks/useFitImageSnapPan';
import FormationSlotImgLayer from './FormationSlotImgLayer';
import type { FormationSlotPlacement } from './formationGrid';

export interface FormationSlotProps {
  peId: string;
  item: FormationItemData;
  placement: FormationSlotPlacement;
  isEditMode: boolean;
}

const FormationSlot: React.FC<FormationSlotProps> = ({
  peId,
  item,
  placement,
  isEditMode,
}) => {
  const updateFormationSlotImage = useProjectElementListStore((s) => s.updateFormationSlotImage);
  const updateFormationSlotFocus = useProjectElementListStore((s) => s.updateFormationSlotFocus);
  const updateFormationSlotImageMode = useProjectElementListStore(
    (s) => s.updateFormationSlotImageMode,
  );
  const updateFormationSlotAlignment = useProjectElementListStore(
    (s) => s.updateFormationSlotAlignment,
  );
  const resolved = resolveFormationSlotImage(item);
  const focusX = resolved?.focusX ?? item.focusX;
  const focusY = resolved?.focusY ?? item.focusY;
  const imageMode = getFormationImageMode(item);
  const horizontalAlignment = getFormationHorizontalAlignment(item);
  const verticalAlignment = getFormationVerticalAlignment(item);
  const imagePosition = getFormationImagePosition(item);
  const backgroundUrl = resolved ? convertS3UrlToCloudFrontUrl(resolved.originUrl) : '';
  const imageWidth = resolved?.width ?? 0;
  const imageHeight = resolved?.height ?? 0;

  const handleFocusChange = useCallback(
    (nextFocusX: number, nextFocusY: number) => {
      updateFormationSlotFocus(peId, item.slotIndex, nextFocusX, nextFocusY);
    },
    [item.slotIndex, peId, updateFormationSlotFocus],
  );

  const coverPan = useCoverImagePan({
    imageWidth,
    imageHeight,
    focusX,
    focusY,
    isEnabled: isEditMode && !!resolved && imageMode === FormationImageMode.FILL,
    onFocusChange: handleFocusChange,
  });

  const handleImageChange = isEditMode
    ? (originUrl: string, width: number, height: number, fileName: string) =>
        updateFormationSlotImage(
          peId,
          item.slotIndex,
          originUrl,
          width,
          height,
          removeFileExtensionWithinLimit(fileName, TEXTBLOCK_CAPTION_MAX_LENGTH),
        )
    : undefined;

  const handleImageModeChange = useCallback(
    (nextImageMode: FormationImageMode) => {
      updateFormationSlotImageMode(peId, item.slotIndex, nextImageMode);
    },
    [item.slotIndex, peId, updateFormationSlotImageMode],
  );

  const handleAlignmentChange = useCallback(
    (
      nextHorizontalAlignment: FormationHorizontalAlignment,
      nextVerticalAlignment: FormationVerticalAlignment,
    ) => {
      updateFormationSlotAlignment(
        peId,
        item.slotIndex,
        nextHorizontalAlignment,
        nextVerticalAlignment,
      );
    },
    [item.slotIndex, peId, updateFormationSlotAlignment],
  );

  const fitPan = useFitImageSnapPan({
    imageWidth,
    imageHeight,
    horizontalAlignment,
    verticalAlignment,
    isEnabled: isEditMode && !!resolved && imageMode === FormationImageMode.FIT,
    onAlignmentChange: handleAlignmentChange,
  });
  const activePan = imageMode === FormationImageMode.FILL ? coverPan : fitPan;

  return (
    <SlotCell
      data-formation-slot-index={item.slotIndex}
      $gridColumn={placement.gridColumn}
      $gridRow={placement.gridRow}
      $isEditMode={isEditMode}
      $hasImage={!!resolved}
      $imageMode={imageMode}
    >
      <FormationSlotImgLayer
        peId={peId}
        slotIndex={item.slotIndex}
        backgroundImg={backgroundUrl}
        imageWidth={imageWidth}
        imageHeight={imageHeight}
        imageMode={imageMode}
        positionX={imagePosition.xPercent}
        positionY={imagePosition.yPercent}
        isEditMode={isEditMode}
        isEmpty={!resolved}
        canPan={activePan.canPan}
        isDragging={activePan.isDragging}
        handleChange={handleImageChange}
        handleImageModeChange={handleImageModeChange}
        panHandlers={activePan.panHandlers}
        aria-label={resolved?.alt}
      />
    </SlotCell>
  );
};

const SlotCell = styled.div<{
  $gridColumn: string;
  $gridRow: string;
  $isEditMode: boolean;
  $hasImage: boolean;
  $imageMode: FormationImageMode;
}>`
  position: relative;
  overflow: hidden;
  min-width: 0;
  width: auto;
  max-width: 100%;
  min-height: 0;
  height: 100%;
  align-self: stretch;
  grid-column: ${({ $gridColumn }) => $gridColumn};
  grid-row: ${({ $gridRow }) => $gridRow};
  background-color: ${({ $hasImage, $imageMode, $isEditMode, theme }) => (
    !$isEditMode && $hasImage && $imageMode === FormationImageMode.FIT
      ? 'transparent'
      : $isEditMode ? theme.colors.color_Gray_06 : theme.colors.color_White
  )};
  transition: height 0.35s ease-in-out;
`;

export default FormationSlot;
